import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useSubscription } from "@apollo/client";
import { useSnackbar } from "notistack";
import { useTranslation, useUser } from "@/shared/hooks";
import type {
  LiveReactionEmojiType,
  LiveReactionsBurstSubscriptionType,
  LiveReactionsBurstSubscriptionVariablesType,
  SendLiveReactionsMutationType,
  SendLiveReactionsMutationVariablesType,
} from "@/types/graphql/site.generated";
import {
  LiveReactionsBurstSubscription,
  SendLiveReactionsMutation,
} from "../api/graphql";
import {
  emptyBatch,
  SEND_DELAY_MS,
  toSendVariables,
  type LocalReaction,
  type ReactionBatch,
} from "../lib/batch";
import { expandBurst, toFlyingItem, type FlyingItem } from "../lib/expandBurst";
import { burstTotal, emptyPending, subtractOwn } from "../lib/subtractOwn";
import { isChatBannedError } from "../lib/isChatBannedError";
import { appendFlyingItems } from "../lib/flyingItems";
import {
  applyOwnReaction,
  emptyPendingState,
  isPendingStale,
  type PendingState,
} from "../lib/ownReaction";

/** Сколько живёт вклад в тихий счётчик (prefers-reduced-motion). */
const RECENT_WINDOW_MS = 5000;

const isTabHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

interface UseLiveReactionsArgs {
  contentId: string;
  phrasesCount: number;
  /** Набор эмодзи платформы: отправляется только он. */
  allowedEmojis: readonly LiveReactionEmojiType[];
  /** false при prefers-reduced-motion и во время каста: без полёта. */
  animate: boolean;
}

export function useLiveReactions({
  contentId,
  phrasesCount,
  allowedEmojis,
  animate,
}: UseLiveReactionsArgs) {
  const { user, openSignIn } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [items, setItems] = useState<FlyingItem[]>([]);
  const [recentTotal, setRecentTotal] = useState(0);
  const batchRef = useRef<ReactionBatch>(emptyBatch());
  const pendingRef = useRef<PendingState>(emptyPendingState());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bannedToastShownRef = useRef(false);
  const sequenceRef = useRef(0);

  const [sendReactions] = useMutation<
    SendLiveReactionsMutationType,
    SendLiveReactionsMutationVariablesType
  >(SendLiveReactionsMutation);

  const bumpRecent = useCallback((count: number) => {
    if (count <= 0) return;
    setRecentTotal((value) => value + count);
    setTimeout(
      () => setRecentTotal((value) => Math.max(0, value - count)),
      RECENT_WINDOW_MS,
    );
  }, []);

  const flush = useCallback(() => {
    timerRef.current = null;
    const variables = toSendVariables(
      contentId,
      batchRef.current,
      phrasesCount,
      allowedEmojis,
    );
    batchRef.current = emptyBatch();
    if (!variables) return;
    // Ошибки мутации гасятся молча, кроме бана в чате: о нём тост один раз.
    // Отклонённое сервер в пачку не положит, ждать своё больше нечего.
    sendReactions({ variables }).catch((error: unknown) => {
      pendingRef.current = emptyPendingState();
      if (isChatBannedError(error) && !bannedToastShownRef.current) {
        bannedToastShownRef.current = true;
        enqueueSnackbar(t("liveReactions.banned"), { variant: "warning" });
      }
    });
  }, [
    contentId,
    phrasesCount,
    allowedEmojis,
    sendReactions,
    enqueueSnackbar,
    t,
  ]);

  const react = useCallback(
    (reaction: LocalReaction) => {
      if (!user) {
        openSignIn();
        return;
      }
      const applied = applyOwnReaction(
        batchRef.current,
        pendingRef.current,
        reaction,
        Date.now(),
      );
      // Сверх лимита пачки нажатие не уходит на сервер: не рисуем его.
      if (!applied) return;
      batchRef.current = applied.batch;
      pendingRef.current = applied.state;

      sequenceRef.current += 1;
      if (animate) {
        const item = toFlyingItem(reaction, `own-${sequenceRef.current}`);
        setItems((prev) =>
          appendFlyingItems(prev, [item], { hidden: isTabHidden() }),
        );
      }
      bumpRecent(1);

      if (!timerRef.current) {
        timerRef.current = setTimeout(flush, SEND_DELAY_MS);
      }
    },
    [user, openSignIn, animate, bumpRecent, flush],
  );

  useSubscription<
    LiveReactionsBurstSubscriptionType,
    LiveReactionsBurstSubscriptionVariablesType
  >(LiveReactionsBurstSubscription, {
    variables: { contentId },
    onData: ({ data }) => {
      const burst = data.data?.liveReactionsBurst;
      if (!burst) return;

      const stale = isPendingStale(pendingRef.current, Date.now());
      const { burst: others, pending } = subtractOwn(
        burst,
        stale ? emptyPending() : pendingRef.current.pending,
      );
      pendingRef.current = { pending, since: pendingRef.current.since };
      bumpRecent(burstTotal(others));

      if (!animate) return;
      sequenceRef.current += 1;
      const keyPrefix = `burst-${sequenceRef.current}`;
      const hidden = isTabHidden();
      setItems((prev) =>
        appendFlyingItems(
          prev,
          expandBurst(others, {
            activeBubbles: prev.filter((item) => item.kind === "phrase").length,
            keyPrefix,
          }),
          { hidden },
        ),
      );
    },
  });

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [],
  );

  // Каст или reduced motion: слой полёта размонтирован, onAnimationEnd не
  // придёт, поэтому уже летящие значки сбрасываем сразу.
  useEffect(() => {
    if (!animate) setItems([]);
  }, [animate]);

  const removeItem = useCallback((key: string) => {
    setItems((prev) => prev.filter((item) => item.key !== key));
  }, []);

  return { items, react, removeItem, recentTotal };
}
